import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  Linking,
  TextInput,
  Image,
} from 'react-native';

const HubitaLogo = require('../../assets/images/Logo/HubitaWeb.png');

export interface Deal {
  id: string;
  badge: string;
  title: string;
  description: string;
  buttonText: string;
  category: 'utilities' | 'broadband' | 'insurance' | 'cleaning' | 'all';
  saving?: string;
  url?: string;
  price?: string;
  icon?: string;
}

const ALL_DEALS: Deal[] = [
  {
    id: 'energy',
    badge: 'Hubita Verified',
    title: 'Gas + Electricity',
    description: "Compare UK tariffs and save up to £135/year on 100% green energy.",
    buttonText: 'Switch Now',
    category: 'utilities',
    saving: 'Save £135/yr',
    url: 'https://partners.staging.home.cc/tlyfe/external/services',
    icon: '⚡',
  },
  {
    id: 'broadband',
    badge: 'Hubita Verified',
    title: 'Fibre Broadband',
    description: "High-speed WiFi ready for move-in day. 100+ Mbps, no setup lag.",
    buttonText: 'Switch Now',
    category: 'broadband',
    saving: 'From £22/mo',
    url: 'https://partners.staging.home.cc/tlyfe/external/services',
    icon: '📶',
  },
  {
    id: 'insurance',
    badge: 'Tenant Protect',
    title: 'Contents & Liability Insurance',
    description: 'Protect your deposit and personal belongings against accidental damage.',
    buttonText: 'Get Quote',
    category: 'insurance',
    saving: 'From £5/mo',
    url: 'https://www.safehouse-getquote.co.uk/TCI',
    icon: '🛡️',
  },
  {
    id: 'cleaning',
    badge: 'Exclusive Deal',
    title: 'End of Tenancy Cleaning',
    description: 'Professional 5-star cleaning service with 100% deposit return guarantee.',
    buttonText: 'Book Clean',
    category: 'cleaning',
    saving: '15% Off',
    url: 'https://partners.staging.home.cc/tlyfe/external/services',
    icon: '✨',
  },
];

export const MarketplaceWidget = ({ route, variant: propVariant }: any) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeDealId, setActiveDealId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Extract variant from query params or route params
  const variant = useMemo(() => {
    let typeParam = route?.params?.type || route?.params?.variant;

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        const search = new URLSearchParams(window.location.search);
        typeParam = search.get('type') || search.get('variant') || typeParam;
      } catch (e) {
        // ignore url parsing error
      }
    }

    if (typeParam) {
      const normalized = typeParam.toString().toLowerCase();
      if (['primary', 'secondary', 'tile', 'services'].includes(normalized)) {
        return normalized;
      }
    }

    if (propVariant) return propVariant.toLowerCase();

    const routeName = (route?.name || '').toLowerCase();
    if (routeName.includes('primary')) return 'primary';
    if (routeName.includes('tile')) return 'tile';
    if (routeName.includes('service')) return 'services';

    return 'secondary';
  }, [propVariant, route?.params, route?.name]);

  // Extract query parameters from React Navigation or window.location
  const queryParams = useMemo(() => {
    let postcode = route?.params?.postcode;
    let firstName = route?.params?.first_name;
    let leadRef = route?.params?.lead_reference;
    let attributionCode = route?.params?.attribution_code;

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        const search = new URLSearchParams(window.location.search);
        postcode = postcode || search.get('postcode') || search.get('postCode');
        firstName = firstName || search.get('first_name');
        leadRef = leadRef || search.get('lead_reference');
        attributionCode = attributionCode || search.get('attribution_code');
      } catch (e) {
        // ignore search error
      }
    }

    return {
      postcode: postcode || 'e140bt',
      firstName: firstName || '',
      leadRef: leadRef || '',
      attributionCode: attributionCode || '',
    };
  }, [route?.params]);

  // Filter deals for 'services' full catalog view
  const filteredDeals = useMemo(() => {
    return ALL_DEALS.filter((deal) => {
      const matchesCategory =
        selectedCategory === 'all' || deal.category === selectedCategory;
      const matchesSearch =
        !searchQuery ||
        deal.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        deal.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  const handleActionClick = (deal: Deal) => {
    setActiveDealId(deal.id);
    setNotice(`Connecting to ${deal.title}...`);

    const payload = JSON.stringify({
      type: 'iframe_click',
      dealId: deal.id,
      val: {
        type: 'doctoopen_inapp',
        Url: deal.url || (Platform.OS === 'web' && typeof window !== 'undefined' ? `${window.location.origin}/tlyfe/external/services` : ''),
      },
    });

    // Notify parent window (ODSHomeBoxTile / ODSHomeIframe)
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(payload, '*');
      }
      const rnWebView = (window as any).ReactNativeWebView;
      if (rnWebView && typeof rnWebView.postMessage === 'function') {
        rnWebView.postMessage(payload);
      }
    }

    setTimeout(() => {
      if (deal.url) {
        if (Platform.OS === 'web' && typeof window !== 'undefined' && window.parent === window) {
          window.open(deal.url, '_blank');
        } else {
          Linking.openURL(deal.url).catch(() => {});
        }
      }
      setNotice(null);
    }, 600);
  };

  const handleExploreAll = () => {
    const payload = JSON.stringify({
      type: 'iframe_click',
      val: {
        type: 'doctoopen_inapp',
        Url: Platform.OS === 'web' && typeof window !== 'undefined' ? `${window.location.origin}/tlyfe/external/services` : '',
      },
    });
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(payload, '*');
      }
    }
  };

  // Reusable Branded Hubita Footer
  const renderHubitaFooter = (extraText = 'Verified Partner Rates') => (
    <View style={styles.brandedFooter}>
      <View style={styles.trustBadgeRow}>
        <View style={styles.verifiedDot} />
        <Text style={styles.verifiedText}>{extraText}</Text>
      </View>
      <View style={styles.hubitaBrandRow}>
        <Text style={styles.poweredByText}>Powered by</Text>
        <Image
          source={HubitaLogo}
          style={styles.hubitaFooterLogo}
          resizeMode="contain"
        />
      </View>
    </View>
  );

  // -------------------------------------------------------------
  // VARIANT 1: TILE (Concierge Teaser - Match screenshot)
  // -------------------------------------------------------------
  if (variant === 'tile') {
    return (
      <View style={styles.tileContainer}>
        {/* Top Header */}
        <View style={styles.tileHeader}>
          <View style={styles.tileBadgeRow}>
            <View style={styles.hubitaConciergeBadge}>
              <Text style={styles.hubitaConciergeText}>HUBITA CONCIERGE</Text>
            </View>
            <View style={styles.perkPill}>
              <Text style={styles.perkPillText}>Free Move-in Setup</Text>
            </View>
          </View>
          <Text style={styles.tileTitle}>
            Setup Your Home for {queryParams.postcode.toUpperCase()}
          </Text>
          <Text style={styles.tileSubtitle}>
            Pre-negotiated utility discounts & move-in setup ready before tenancy start.
          </Text>
        </View>

        {/* 4 Feature Micro-Cards */}
        <View style={styles.tileFeaturesGrid}>
          {/* Energy */}
          <TouchableOpacity
            style={styles.tileFeatureCard}
            onPress={() => handleActionClick(ALL_DEALS[0])}
            activeOpacity={0.8}
          >
            <View style={[styles.tileIconCircle, { backgroundColor: '#FEF3C7' }]}>
              <Text style={styles.tileEmoji}>⚡</Text>
            </View>
            <Text style={styles.tileFeatureTitle}>Energy</Text>
            <Text style={[styles.tileFeatureHighlight, { color: '#D97706' }]}>Save £135</Text>
          </TouchableOpacity>

          {/* Broadband */}
          <TouchableOpacity
            style={styles.tileFeatureCard}
            onPress={() => handleActionClick(ALL_DEALS[1])}
            activeOpacity={0.8}
          >
            <View style={[styles.tileIconCircle, { backgroundColor: '#E0F2FE' }]}>
              <Text style={styles.tileEmoji}>📶</Text>
            </View>
            <Text style={styles.tileFeatureTitle}>WiFi</Text>
            <Text style={[styles.tileFeatureHighlight, { color: '#0284C7' }]}>From £22/m</Text>
          </TouchableOpacity>

          {/* Insurance */}
          <TouchableOpacity
            style={styles.tileFeatureCard}
            onPress={() => handleActionClick(ALL_DEALS[2])}
            activeOpacity={0.8}
          >
            <View style={[styles.tileIconCircle, { backgroundColor: '#F3E8FF' }]}>
              <Text style={styles.tileEmoji}>🛡️</Text>
            </View>
            <Text style={styles.tileFeatureTitle}>Insurance</Text>
            <Text style={[styles.tileFeatureHighlight, { color: '#7C3AED' }]}>From £5/m</Text>
          </TouchableOpacity>

          {/* Cleaning */}
          <TouchableOpacity
            style={styles.tileFeatureCard}
            onPress={() => handleActionClick(ALL_DEALS[3])}
            activeOpacity={0.8}
          >
            <View style={[styles.tileIconCircle, { backgroundColor: '#D1FAE5' }]}>
              <Text style={styles.tileEmoji}>✨</Text>
            </View>
            <Text style={styles.tileFeatureTitle}>Cleaning</Text>
            <Text style={[styles.tileFeatureHighlight, { color: '#059669' }]}>15% Off</Text>
          </TouchableOpacity>
        </View>

        {/* Action Button */}
        <TouchableOpacity
          activeOpacity={0.85}
          style={styles.primaryActionButton}
          onPress={() => handleActionClick(ALL_DEALS[0])}
        >
          <Text style={styles.primaryActionButtonText}>Explore Available Deals →</Text>
        </TouchableOpacity>

        {/* Branded Footer with Hubita Logo */}
        {renderHubitaFooter('Instant Setup • Cancel Anytime')}
      </View>
    );
  }

  // -------------------------------------------------------------
  // VARIANT 2: SECONDARY (Deals View - 2 Featured side-by-side)
  // -------------------------------------------------------------
  if (variant === 'secondary') {
    return (
      <View style={styles.dealsContainer}>
        {/* Header */}
        <View style={styles.dealsHeader}>
          <View>
            <Text style={styles.dealsTitle}>Top Deals for {queryParams.postcode.toUpperCase()}</Text>
            <Text style={styles.dealsSubtitle}>Pre-negotiated utility discounts for your tenancy</Text>
          </View>
          <TouchableOpacity onPress={handleExploreAll} style={styles.viewAllPill}>
            <Text style={styles.viewAllPillText}>View All →</Text>
          </TouchableOpacity>
        </View>

        {/* 2 Deal Cards */}
        <View style={styles.dealsGrid}>
          {ALL_DEALS.slice(0, 2).map((deal) => (
            <View key={deal.id} style={styles.dealCard}>
              <View style={styles.cardBadgeRow}>
                <View style={styles.dealBadge}>
                  <Text style={styles.dealBadgeText}>{deal.badge}</Text>
                </View>
                {deal.saving && (
                  <View style={styles.savingBadge}>
                    <Text style={styles.savingBadgeText}>{deal.saving}</Text>
                  </View>
                )}
              </View>

              <View style={styles.dealInfo}>
                <View style={styles.dealTitleRow}>
                  <Text style={styles.dealEmoji}>{deal.icon}</Text>
                  <Text style={styles.dealTitle} numberOfLines={1}>{deal.title}</Text>
                </View>
                <Text style={styles.dealDescription} numberOfLines={2}>
                  {deal.description}
                </Text>
              </View>

              <TouchableOpacity
                activeOpacity={0.85}
                style={styles.cardButton}
                onPress={() => handleActionClick(deal)}
              >
                <Text style={styles.cardButtonText}>{deal.buttonText}</Text>
              </TouchableOpacity>
            </View>
          ))}
        </View>

        {/* Branded Footer with Hubita Logo */}
        {renderHubitaFooter('🔒 Secure Zero-Spam Switching')}
      </View>
    );
  }

  // -------------------------------------------------------------
  // VARIANT 3: PRIMARY (Compact Essentials - Rich 2x2 Layout)
  // -------------------------------------------------------------
  if (variant === 'primary') {
    return (
      <View style={styles.primaryContainer}>
        {/* Banner */}
        <View style={styles.primaryBanner}>
          <View style={styles.primaryHeaderRow}>
            <View style={styles.hubitaConciergeBadge}>
              <Text style={styles.hubitaConciergeText}>⚡ TENANT PERKS</Text>
            </View>
            <Text style={styles.primarySavingsTag}>Save £300+ / yr</Text>
          </View>
          <Text style={styles.primaryTitle}>Exclusive Move-In Essentials</Text>
          <Text style={styles.primarySubtitle}>
            Pre-negotiated utility discounts verified for {queryParams.postcode.toUpperCase()}
          </Text>
        </View>

        {/* 2x2 Micro Cards Grid */}
        <View style={styles.primaryGrid2x2}>
          {/* Card 1: Energy */}
          <TouchableOpacity
            style={styles.primaryMiniCard}
            onPress={() => handleActionClick(ALL_DEALS[0])}
            activeOpacity={0.8}
          >
            <View style={[styles.primaryMiniIcon, { backgroundColor: '#FEF3C7' }]}>
              <Text style={{ fontSize: 14 }}>⚡</Text>
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.primaryMiniTitle} numberOfLines={1}>Green Energy</Text>
              <Text style={styles.primaryMiniSaving}>Save £135/yr</Text>
            </View>
            <Text style={styles.primaryMiniArrow}>→</Text>
          </TouchableOpacity>

          {/* Card 2: Broadband */}
          <TouchableOpacity
            style={styles.primaryMiniCard}
            onPress={() => handleActionClick(ALL_DEALS[1])}
            activeOpacity={0.8}
          >
            <View style={[styles.primaryMiniIcon, { backgroundColor: '#E0F2FE' }]}>
              <Text style={{ fontSize: 14 }}>📶</Text>
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.primaryMiniTitle} numberOfLines={1}>Fibre WiFi 108M</Text>
              <Text style={[styles.primaryMiniSaving, { color: '#0284C7' }]}>From £22/mo</Text>
            </View>
            <Text style={styles.primaryMiniArrow}>→</Text>
          </TouchableOpacity>

          {/* Card 3: Insurance */}
          <TouchableOpacity
            style={styles.primaryMiniCard}
            onPress={() => handleActionClick(ALL_DEALS[2])}
            activeOpacity={0.8}
          >
            <View style={[styles.primaryMiniIcon, { backgroundColor: '#F3E8FF' }]}>
              <Text style={{ fontSize: 14 }}>🛡️</Text>
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.primaryMiniTitle} numberOfLines={1}>Tenants Protect</Text>
              <Text style={[styles.primaryMiniSaving, { color: '#7C3AED' }]}>From £5/mo</Text>
            </View>
            <Text style={styles.primaryMiniArrow}>→</Text>
          </TouchableOpacity>

          {/* Card 4: Cleaning */}
          <TouchableOpacity
            style={styles.primaryMiniCard}
            onPress={() => handleActionClick(ALL_DEALS[3])}
            activeOpacity={0.8}
          >
            <View style={[styles.primaryMiniIcon, { backgroundColor: '#D1FAE5' }]}>
              <Text style={{ fontSize: 14 }}>✨</Text>
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.primaryMiniTitle} numberOfLines={1}>Tenancy Clean</Text>
              <Text style={[styles.primaryMiniSaving, { color: '#059669' }]}>15% Off</Text>
            </View>
            <Text style={styles.primaryMiniArrow}>→</Text>
          </TouchableOpacity>
        </View>

        {/* Action Button & Micro-trust */}
        <View style={{ marginTop: 6 }}>
          <TouchableOpacity
            activeOpacity={0.85}
            style={styles.primaryActionButton}
            onPress={() => handleActionClick(ALL_DEALS[0])}
          >
            <Text style={styles.primaryActionButtonText}>
              Unlock All Deals for {queryParams.postcode.toUpperCase()} →
            </Text>
          </TouchableOpacity>
          <Text style={styles.primaryTrustNotice}>
            Free concierge service • Cancel anytime • Zero credit impact
          </Text>
        </View>

        {/* Branded Footer with Hubita Logo */}
        {renderHubitaFooter('4.9★ Verified Tenant Rates')}
      </View>
    );
  }

  // -------------------------------------------------------------
  // VARIANT 4: SERVICES (4-Pillar Directory)
  // -------------------------------------------------------------
  return (
    <View style={styles.servicesContainer}>
      <View style={styles.servicesHeader}>
        <View>
          <Text style={styles.servicesTitle}>Home Setup Directory</Text>
          <Text style={styles.servicesSubtitle}>
            Everything you need for {queryParams.postcode.toUpperCase()}
          </Text>
        </View>
        <View style={styles.servicesPill}>
          <Text style={styles.servicesPillText}>4 Available</Text>
        </View>
      </View>

      <View style={styles.servicesGrid}>
        {ALL_DEALS.map((deal) => (
          <TouchableOpacity
            key={deal.id}
            style={styles.serviceItemCard}
            onPress={() => handleActionClick(deal)}
            activeOpacity={0.8}
          >
            <View style={styles.serviceIconWrap}>
              <Text style={{ fontSize: 18 }}>{deal.icon}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.serviceItemTitle} numberOfLines={1}>{deal.title}</Text>
              <Text style={styles.serviceItemSaving}>{deal.saving}</Text>
            </View>
            <View style={styles.serviceArrowCircle}>
              <Text style={styles.serviceArrowText}>→</Text>
            </View>
          </TouchableOpacity>
        ))}
      </View>

      {/* Branded Footer with Hubita Logo */}
      {renderHubitaFooter('Official Moving Concierge')}
    </View>
  );
};

const styles = StyleSheet.create({
  // -------------------------------------------------------------
  // Reusable Hubita Footer Styles
  // -------------------------------------------------------------
  brandedFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 8,
    marginTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
    height: 32,
  },
  trustBadgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  verifiedDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#10B981',
  },
  verifiedText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#64748B',
  },
  hubitaBrandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  poweredByText: {
    fontSize: 10,
    color: '#94A3B8',
    fontWeight: '500',
  },
  hubitaFooterLogo: {
    width: 78,
    height: 18,
  },

  // -------------------------------------------------------------
  // Variant 1: TILE (Concierge Teaser)
  // -------------------------------------------------------------
  tileContainer: {
    flex: 1,
    height: 360,
    maxHeight: 360,
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    paddingHorizontal: 18,
    paddingTop: 16,
    paddingBottom: 10,
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    overflow: 'hidden',
  },
  tileHeader: {
    marginBottom: 6,
  },
  tileBadgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  hubitaConciergeBadge: {
    backgroundColor: '#F3E8FF',
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E9D5FF',
  },
  hubitaConciergeText: {
    fontSize: 10,
    fontWeight: '800',
    color: '#6C2BD9',
    letterSpacing: 0.4,
  },
  perkPill: {
    backgroundColor: '#DCFCE7',
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 10,
  },
  perkPillText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#15803D',
  },
  tileTitle: {
    fontSize: 17,
    fontWeight: '800',
    color: '#0F172A',
    letterSpacing: -0.3,
    marginBottom: 4,
  },
  tileSubtitle: {
    fontSize: 12,
    color: '#64748B',
    lineHeight: 16,
  },
  tileFeaturesGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
    marginVertical: 10,
  },
  tileFeatureCard: {
    flex: 1,
    backgroundColor: '#F8FAFC',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 6,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#F1F5F9',
  },
  tileIconCircle: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 6,
  },
  tileEmoji: {
    fontSize: 16,
  },
  tileFeatureTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 2,
  },
  tileFeatureHighlight: {
    fontSize: 9,
    fontWeight: '700',
  },
  primaryActionButton: {
    backgroundColor: '#6C2BD9',
    paddingVertical: 11,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#6C2BD9',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
  },
  primaryActionButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.2,
  },

  // -------------------------------------------------------------
  // Variant 2: SECONDARY (Deals View)
  // -------------------------------------------------------------
  dealsContainer: {
    flex: 1,
    height: 360,
    maxHeight: 360,
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 10,
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    overflow: 'hidden',
  },
  dealsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  dealsTitle: {
    fontSize: 15,
    fontWeight: '800',
    color: '#0F172A',
    letterSpacing: -0.2,
  },
  dealsSubtitle: {
    fontSize: 11,
    color: '#64748B',
    marginTop: 2,
  },
  viewAllPill: {
    backgroundColor: '#F3E8FF',
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
  },
  viewAllPillText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#6C2BD9',
  },
  dealsGrid: {
    flexDirection: 'row',
    gap: 10,
    flex: 1,
    marginVertical: 4,
  },
  dealCard: {
    flex: 1,
    backgroundColor: '#F8FAFC',
    borderRadius: 10,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    justifyContent: 'space-between',
  },
  cardBadgeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  dealBadge: {
    backgroundColor: '#F3E8FF',
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4,
  },
  dealBadgeText: {
    fontSize: 9,
    fontWeight: '800',
    color: '#6C2BD9',
  },
  savingBadge: {
    backgroundColor: '#DCFCE7',
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4,
  },
  savingBadgeText: {
    fontSize: 9,
    fontWeight: '800',
    color: '#15803D',
  },
  dealInfo: {
    flex: 1,
    justifyContent: 'center',
    marginVertical: 4,
  },
  dealTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 4,
  },
  dealEmoji: {
    fontSize: 14,
  },
  dealTitle: {
    fontSize: 13,
    fontWeight: '800',
    color: '#1E293B',
  },
  dealDescription: {
    fontSize: 11,
    color: '#64748B',
    lineHeight: 15,
  },
  cardButton: {
    backgroundColor: '#6C2BD9',
    paddingVertical: 8,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 6,
  },
  cardButtonText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: '700',
  },

  // -------------------------------------------------------------
  // Variant 3: PRIMARY (Compact Essentials)
  // -------------------------------------------------------------
  primaryContainer: {
    flex: 1,
    height: 360,
    maxHeight: 360,
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 10,
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    overflow: 'hidden',
  },
  primaryBanner: {
    marginBottom: 8,
  },
  primaryHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  primarySavingsTag: {
    fontSize: 10,
    fontWeight: '800',
    color: '#15803D',
    backgroundColor: '#DCFCE7',
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 10,
  },
  primaryTitle: {
    fontSize: 16,
    fontWeight: '800',
    color: '#0F172A',
    marginBottom: 4,
  },
  primarySubtitle: {
    fontSize: 11,
    color: '#64748B',
    lineHeight: 15,
  },
  primaryGrid2x2: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginVertical: 4,
  },
  primaryMiniCard: {
    width: '48.5%',
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    paddingVertical: 7,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  primaryMiniIcon: {
    width: 28,
    height: 28,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryMiniTitle: {
    fontSize: 11,
    fontWeight: '800',
    color: '#1E293B',
  },
  primaryMiniSaving: {
    fontSize: 9,
    fontWeight: '700',
    color: '#15803D',
  },
  primaryMiniArrow: {
    fontSize: 11,
    fontWeight: '800',
    color: '#6C2BD9',
  },
  primaryTrustNotice: {
    textAlign: 'center',
    fontSize: 9,
    color: '#94A3B8',
    marginTop: 4,
  },

  // -------------------------------------------------------------
  // Variant 4: SERVICES (Directory)
  // -------------------------------------------------------------
  servicesContainer: {
    flex: 1,
    height: 360,
    maxHeight: 360,
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 10,
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    overflow: 'hidden',
  },
  servicesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  servicesTitle: {
    fontSize: 15,
    fontWeight: '800',
    color: '#0F172A',
  },
  servicesSubtitle: {
    fontSize: 11,
    color: '#64748B',
  },
  servicesPill: {
    backgroundColor: '#DCFCE7',
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 10,
  },
  servicesPillText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#15803D',
  },
  servicesGrid: {
    flex: 1,
    justifyContent: 'center',
    gap: 6,
  },
  serviceItemCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#F8FAFC',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#F1F5F9',
  },
  serviceIconWrap: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  serviceItemTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: '#1E293B',
  },
  serviceItemSaving: {
    fontSize: 10,
    color: '#15803D',
    fontWeight: '600',
  },
  serviceArrowCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#EDE9FE',
    alignItems: 'center',
    justifyContent: 'center',
  },
  serviceArrowText: {
    fontSize: 11,
    fontWeight: '800',
    color: '#6C2BD9',
  },
});

export default MarketplaceWidget;
